import { ButtonColor } from '@mm/constants/button-color';
import classnames from 'classnames';
import React, { MouseEvent, MutableRefObject } from 'react';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import styles from './Button.module.css';

export type ButtonProps = {
  children?: React.ReactNode;
  css?: {
    container?: string;
    leftIcon?: string;
    rightIcon?: string;
  };
  disabled?: boolean;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => any;
  onMouseDown?: (e: MouseEvent<HTMLButtonElement>) => void;
  color?: ButtonColor;
  type?: 'submit' | 'reset' | 'button';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  id?: string;
  size?: 'sm' | 'md' | 'lg';
  testId?: string;
  whitelabelCompanyHash?: string;
};

export const Button = React.forwardRef(
  (props: ButtonProps, ref: MutableRefObject<HTMLButtonElement | null>) => {
    const {
      css,
      disabled,
      onClick,
      onMouseDown,
      children,
      leftIcon,
      rightIcon,
      id,
      color = 'blue',
      type = 'button',
      size = 'md',
      testId,
      whitelabelCompanyHash,
    } = props;

    const hasChildren =
      children && (!Array.isArray(children) || children.filter(Boolean).length);

    const {
      primaryButtonStyle,
      secondaryButtonStyle,
      primaryColor,
      getCompanyConfig,
    } = useWhitelabelConfig();
    const {
      primaryButtonStyle: whitelabelPrimaryBtnStyle,
      primaryColor: whitelabelPrimaryColor,
      secondaryButtonStyle: whitelabelSecondaryColor,
    } = getCompanyConfig?.(undefined, whitelabelCompanyHash) || {};

    const whitelabelStyles =
      color === 'blue'
        ? whitelabelPrimaryBtnStyle || primaryButtonStyle
        : color.includes('transparent')
        ? { color: whitelabelPrimaryColor || primaryColor }
        : whitelabelSecondaryColor || secondaryButtonStyle;

    const classList = classnames(
      styles.main,
      css?.container,
      styles[`color--${color}`],
      styles[`size--${size}`],
      {
        [styles.iconOnly]: leftIcon && !hasChildren,
      },
    );

    const clickHandler = (e: MouseEvent<HTMLButtonElement>) => {
      if (!disabled && onClick) {
        onClick(e);
      }
    };

    return (
      <button
        ref={ref}
        className={classList}
        onClick={clickHandler}
        onMouseDown={onMouseDown}
        type={type}
        id={id}
        disabled={disabled}
        data-test={testId}
        style={!disabled ? whitelabelStyles : undefined}
      >
        {leftIcon && (
          <span
            className={classnames(styles.leftIcon, css?.leftIcon)}
            style={whitelabelStyles}
          >
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span
            className={classnames(styles.rightIcon, css?.rightIcon)}
            style={whitelabelStyles}
          >
            {rightIcon}
          </span>
        )}
      </button>
    );
  },
);
